import { useMemo } from 'react';
import type { ImageSourcePropType } from 'react-native';
import { useT } from '@transifex/react';

// As in CSS object-position: 0 pins the left/top edge, 1 the right/bottom.
export type FocalPoint = { x: number; y: number };

export type SlideImage = { source: ImageSourcePropType; aspect: number; focus: FocalPoint };

export type BrandSlide = {
  key: string;
  title: string;
  // Crops of the Figma "Finals main carousel" frames: phone 393×852, tablet panel 794×560, desktop panel 860×860.
  images: SlideImage[];
  // Share of the full top scrim on a phone; the first photo is light enough without one.
  topScrim: number;
};

const PHONE = 922 / 2000;
const TABLET = 1600 / 1129;
const DESKTOP = 1;

export const useBrandSlides = (): BrandSlide[] => {
  const t = useT();

  return useMemo(
    () => [
      {
        key: 'care-plan',
        title: t('A care plan\nbuilt around you.'),
        images: [
          {
            source: require('../../assets/images/brand/care-plan-phone.jpg'),
            aspect: PHONE,
            focus: { x: 0.5, y: 0.25 },
          },
          {
            source: require('../../assets/images/brand/care-plan-tablet.jpg'),
            aspect: TABLET,
            focus: { x: 0.8, y: 0.1 },
          },
          {
            source: require('../../assets/images/brand/care-plan-desktop.jpg'),
            aspect: DESKTOP,
            focus: { x: 0.8, y: 0.3 },
          },
        ],
        topScrim: 0,
      },
      {
        key: 'care-team',
        title: t('A care team\nin your corner'),
        images: [
          {
            source: require('../../assets/images/brand/care-team-phone.jpg'),
            aspect: PHONE,
            focus: { x: 0.5, y: 0.25 },
          },
          {
            source: require('../../assets/images/brand/care-team-tablet.jpg'),
            aspect: TABLET,
            focus: { x: 0.6, y: 0.35 },
          },
          {
            source: require('../../assets/images/brand/care-team-desktop.jpg'),
            aspect: DESKTOP,
            focus: { x: 0.5, y: 0.4 },
          },
        ],
        topScrim: 0.75,
      },
      {
        key: 'progress',
        title: t('Progress you\ncan feel'),
        images: [
          {
            source: require('../../assets/images/brand/progress-phone.jpg'),
            aspect: PHONE,
            focus: { x: 0.5, y: 0.25 },
          },
          {
            source: require('../../assets/images/brand/progress-tablet.jpg'),
            aspect: TABLET,
            focus: { x: 0.7, y: 0.6 },
          },
          {
            source: require('../../assets/images/brand/progress-desktop.jpg'),
            aspect: DESKTOP,
            focus: { x: 0.55, y: 0.5 },
          },
        ],
        topScrim: 1,
      },
      {
        key: 'support',
        title: t('Support for your\nhealth goals'),
        images: [
          {
            source: require('../../assets/images/brand/support-phone.jpg'),
            aspect: PHONE,
            focus: { x: 0.5, y: 0.25 },
          },
          {
            source: require('../../assets/images/brand/support-tablet.jpg'),
            aspect: TABLET,
            focus: { x: 0.6, y: 0.4 },
          },
          {
            source: require('../../assets/images/brand/support-desktop.jpg'),
            aspect: DESKTOP,
            focus: { x: 0.6, y: 0.5 },
          },
        ],
        topScrim: 0.75,
      },
    ],
    [t]
  );
};
